import { daemonStatusServerRequirementSchema } from "./daemon-status-server-requirement.js";
import { archiveProjectResponseSchema, createProjectResponseSchema, listProjectsResponseSchema, projectSchema, updateProjectResponseSchema } from "../http/projects.js";
import { localProjectControlConnectionSchema } from "../daemon/local-project-control.js";
import { createCenterAgentRoutineResponseSchema, deleteCenterAgentRoutineResponseSchema, listCenterAgentRoutineRunsResponseSchema, listCenterAgentRoutinesResponseSchema, pauseCenterAgentRoutineResponseSchema, resumeCenterAgentRoutineResponseSchema, updateCenterAgentRoutineResponseSchema } from "../http/center-agent-routines.js";
import { agentActivitySnapshotResponseSchema } from "../http/agent-activity.js";
import { submitFeedbackIssueRequestSchema, submitFeedbackIssueResponseSchema } from "../http/feedback.js";
import { z } from "zod/v4";
//#region src/schemas/cli/json-output.ts
/**
* Shared contract for the WakeLoop CLI `--json` machine-readable output boundary.
*
* This is the single source of truth for the stdout envelope a non-interactive
* driver (Desktop GUI, scripts, CI) parses when a command is run with `--json`.
* The CLI must derive its envelope types from these schemas via `z.infer`
* instead of hand-writing a parallel shape that can drift
* (`developing/architecture/shared-contracts.md`: "CLI JSON" is a shared
* boundary; "Do not create a separate hand-written shared type that can drift
* from the runtime schema.").
*
* Scope: this owns ONLY the `{ schemaVersion: 1, type, ... }` `--json` envelope.
* It does NOT own the separate `--view agent` presenter protocol (the flat
* `{ type: "error", code, message }` / `{ type: "hints", ... }` lines and the
* `{ type: "result", code, payload }` stream), which is a distinct
* machine-readable surface emitted without `schemaVersion`.
*/
/**
* The CLI `--json` envelope schema version. This versions the envelope wire
* surface independently of `WAKELOOP_SCHEMA_VERSION` and the other component schema
* versions, mirroring `LOCAL_COMPONENTS_SCHEMA_VERSION`, because the `--json`
* boundary evolves on its own cadence. It is currently `1` everywhere the CLI
* emits an envelope.
*/
const CLI_JSON_OUTPUT_SCHEMA_VERSION = 1;
const schemaVersionSchema = z.literal(1);
/**
* Stable error codes a blocked command entry-check emits under `--json`. These
* are the closed set the CLI routes blocked entry states to; defining the set
* here keeps the CLI from minting drift codes. The error envelope itself stays
* open (`error.code` is any string) because errors also originate outside the
* entry-check flow (e.g. `cli.error`, gateway codes).
*/
const cliJsonEntryCheckBlockedErrorCodeSchema = z.enum([
	"auth.authentication_required",
	"profile.required",
	"local_state.repair_required",
	"upgrade.required"
]);
/**
* The failure envelope: the top-level boundary prints exactly this when a
* command throws (or a blocking entry-check fires) under `--json`, then exits 1.
* Fully owned, so it is strict.
*/
const cliJsonErrorEnvelopeSchema = z.strictObject({
	schemaVersion: schemaVersionSchema,
	type: z.literal("error"),
	error: z.strictObject({
		code: z.string(),
		message: z.string(),
		requestId: z.string().optional()
	})
});
/**
* Success envelopes share the `{ schemaVersion, type }` frame and then flatten a
* command-specific result onto the envelope. The deep result shapes (daemon
* install/run/status state, runtime projections, agent registration summaries)
* are owned by their respective daemon / agents runtime contracts. This envelope
* is the FRAME owner: it pins the version literal and the closed `type`
* discriminant, and deliberately RELAXES the flattened payload via
* `z.looseObject` rather than re-deriving those domain-owned shapes here — doing
* so would create a second source of truth for payloads another owner already
* defines. (`shared-contracts.md`: "Document any relaxed object boundary.")
*
* Only fields the CLI authors directly on the envelope (and that carry a stable
* literal or primitive shape) are validated here; everything flattened from a
* domain payload passes through the relaxed boundary.
*/
function defineSuccessEnvelope(type, shape) {
	return z.looseObject({
		schemaVersion: schemaVersionSchema,
		type: z.literal(type),
		...shape
	});
}
function defineStrictSuccessEnvelope(type, shape) {
	return z.strictObject({
		schemaVersion: schemaVersionSchema,
		type: z.literal(type),
		...shape
	});
}
const cliJsonDaemonRunEnvelopeSchema = defineSuccessEnvelope("daemon.run", { ok: z.boolean() });
const cliJsonDaemonInstallEnvelopeSchema = defineSuccessEnvelope("daemon.install", { status: z.string() });
const cliJsonDaemonUninstallEnvelopeSchema = defineSuccessEnvelope("daemon.uninstall", { status: z.enum(["removed", "not_installed"]) });
const cliJsonDaemonReinstallEnvelopeSchema = defineSuccessEnvelope("daemon.reinstall", {});
const cliJsonDaemonStatusEnvelopeSchema = defineSuccessEnvelope("daemon.status", {
	runtimeFlavor: z.enum(["binary", "node-bundle"]).optional(),
	serverRequirement: daemonStatusServerRequirementSchema.optional()
});
const cliJsonDaemonStopEnvelopeSchema = defineSuccessEnvelope("daemon.stop", {});
const cliJsonDaemonAutostartEnvelopeSchema = defineSuccessEnvelope("daemon.autostart", {
	action: z.enum(["enable", "disable"]),
	enabled: z.boolean()
});
const cliJsonAgentsDetectEnvelopeSchema = defineSuccessEnvelope("agents.detect", { count: z.number().int().nonnegative() });
const cliJsonAgentsListEnvelopeSchema = defineSuccessEnvelope("agents.list", { count: z.number().int().nonnegative() });
const cliJsonAgentsShowEnvelopeSchema = defineSuccessEnvelope("agents.show", {});
const cliJsonAgentsEnableEnvelopeSchema = defineSuccessEnvelope("agents.enable", {});
const cliJsonAgentsDisableEnvelopeSchema = defineSuccessEnvelope("agents.disable", {});
const cliJsonFeedbackSubmittedEnvelopeSchema = defineSuccessEnvelope("feedback_submitted", {
	issueId: submitFeedbackIssueResponseSchema.shape.issueId,
	category: submitFeedbackIssueRequestSchema.shape.category,
	submitterKind: submitFeedbackIssueRequestSchema.shape.submitterKind,
	anonymous: z.boolean()
});
const cliJsonAgentActivityLocalBindingSchema = z.strictObject({
	boundAtMs: z.number().int().nonnegative(),
	organizationId: z.string().min(1),
	projectId: z.string().min(1),
	projectSlug: z.string().min(1),
	repositoryFingerprint: z.string().nullable(),
	repositoryPath: z.string().min(1)
});
const cliJsonProjectsListEnvelopeSchema = defineStrictSuccessEnvelope("projects.list", {
	...listProjectsResponseSchema.shape,
	localBindings: z.array(cliJsonAgentActivityLocalBindingSchema)
});
const cliJsonProjectsCreateEnvelopeSchema = defineStrictSuccessEnvelope("projects.create", createProjectResponseSchema.shape);
const cliJsonProjectsUpdateEnvelopeSchema = defineStrictSuccessEnvelope("projects.update", updateProjectResponseSchema.shape);
const cliJsonProjectsArchiveEnvelopeSchema = defineStrictSuccessEnvelope("projects.archive", archiveProjectResponseSchema.shape);
const cliJsonAgentActivityIntegrationUpdateSchema = z.strictObject({
	claude: z.enum([
		"changed",
		"missing",
		"reused"
	]),
	codex: z.enum([
		"changed",
		"missing",
		"reused"
	]),
	openCode: z.enum([
		"changed",
		"missing",
		"reused"
	])
});
const cliJsonProjectsBindEnvelopeSchema = defineStrictSuccessEnvelope("projects.bind", {
	binding: cliJsonAgentActivityLocalBindingSchema,
	integrationSetup: z.discriminatedUnion("status", [z.strictObject({
		integrations: cliJsonAgentActivityIntegrationUpdateSchema,
		status: z.literal("ready")
	}), z.strictObject({
		message: z.string().trim().min(1).max(512),
		status: z.literal("needs_attention")
	})]),
	project: projectSchema
});
const cliJsonProjectsUnbindEnvelopeSchema = defineStrictSuccessEnvelope("projects.unbind", {
	organizationId: z.string().min(1),
	projectSlug: z.string().min(1),
	unbound: z.literal(true)
});
const cliJsonProjectConnectionSnapshotShape = {
	organizationId: z.string().min(1),
	projectId: z.string().min(1),
	projectSlug: z.string().min(1),
	connections: z.array(localProjectControlConnectionSchema)
};
const cliJsonProjectsConnectionsListEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.list", { ...cliJsonProjectConnectionSnapshotShape });
const cliJsonProjectsConnectionsSetDefaultEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.set_default", {
	...cliJsonProjectConnectionSnapshotShape,
	status: z.enum(["updated", "unchanged"])
});
const cliJsonProjectsConnectionsRemoveEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.remove", {
	...cliJsonProjectConnectionSnapshotShape,
	status: z.enum(["removed", "unchanged"])
});
const cliJsonAgentActivityReadEnvelopeSchema = defineStrictSuccessEnvelope("activity.read", agentActivitySnapshotResponseSchema.shape);
const cliJsonAgentActivityIntegrationStatusSchema = z.strictObject({
	claude: z.boolean(),
	codex: z.boolean(),
	openCode: z.boolean()
});
const cliJsonAgentActivityHealthSchema = z.strictObject({
	pending: z.number().int().nonnegative(),
	permanentlyRejected: z.number().int().nonnegative(),
	retrying: z.number().int().nonnegative()
});
const cliJsonAgentActivityInstallEnvelopeSchema = defineStrictSuccessEnvelope("activity.install", { integrations: cliJsonAgentActivityIntegrationUpdateSchema });
const cliJsonAgentActivityUninstallEnvelopeSchema = defineStrictSuccessEnvelope("activity.uninstall", { integrations: cliJsonAgentActivityIntegrationUpdateSchema });
const cliJsonAgentActivityStatusEnvelopeSchema = defineStrictSuccessEnvelope("activity.status", {
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema,
	integrations: cliJsonAgentActivityIntegrationStatusSchema
});
const cliJsonAgentActivityHealthEnvelopeSchema = defineStrictSuccessEnvelope("activity.health", {
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema
});
const cliJsonAgentActivitySyncEnvelopeSchema = defineStrictSuccessEnvelope("activity.sync", { result: z.strictObject({
	controllerTaskEventSubmittedCount: z.number().int().nonnegative(),
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema,
	observedCount: z.number().int().nonnegative(),
	reason: z.enum([
		"service_contract_unavailable",
		"auth_unavailable",
		"registration_rejected",
		"write_auth_rejected",
		"write_endpoint_unavailable",
		"write_retryable_http_failure",
		"write_response_invalid",
		"write_timeout",
		"write_transport_failure"
	]).nullable(),
	status: z.enum([
		"idle",
		"submitted",
		"unavailable"
	]),
	submittedCount: z.number().int().nonnegative()
}) });
const cliJsonAgentActivityContextEnvelopeSchema = defineStrictSuccessEnvelope("activity.context", { context: z.string().min(1).max(300).nullable() });
const cliJsonRoutinesListEnvelopeSchema = defineStrictSuccessEnvelope("routines.list", listCenterAgentRoutinesResponseSchema.shape);
const cliJsonRoutinesCreateEnvelopeSchema = defineStrictSuccessEnvelope("routines.create", createCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesShowEnvelopeSchema = defineStrictSuccessEnvelope("routines.show", createCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesUpdateEnvelopeSchema = defineStrictSuccessEnvelope("routines.update", updateCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesPauseEnvelopeSchema = defineStrictSuccessEnvelope("routines.pause", pauseCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesResumeEnvelopeSchema = defineStrictSuccessEnvelope("routines.resume", resumeCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesDeleteEnvelopeSchema = defineStrictSuccessEnvelope("routines.delete", deleteCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesRunsEnvelopeSchema = defineStrictSuccessEnvelope("routines.runs", listCenterAgentRoutineRunsResponseSchema.shape);
/**
* The full `--json` output envelope: the failure envelope plus every success
* envelope, discriminated on `type`. A `--json` driver dispatches on
* `schemaVersion` + `type`; that frame is the contract this validates.
*/
const cliJsonOutputEnvelopeSchema = z.discriminatedUnion("type", [
	cliJsonErrorEnvelopeSchema,
	cliJsonDaemonRunEnvelopeSchema,
	cliJsonDaemonInstallEnvelopeSchema,
	cliJsonDaemonUninstallEnvelopeSchema,
	cliJsonDaemonReinstallEnvelopeSchema,
	cliJsonDaemonStatusEnvelopeSchema,
	cliJsonDaemonStopEnvelopeSchema,
	cliJsonDaemonAutostartEnvelopeSchema,
	cliJsonAgentsDetectEnvelopeSchema,
	cliJsonAgentsListEnvelopeSchema,
	cliJsonAgentsShowEnvelopeSchema,
	cliJsonAgentsEnableEnvelopeSchema,
	cliJsonAgentsDisableEnvelopeSchema,
	cliJsonFeedbackSubmittedEnvelopeSchema,
	cliJsonProjectsListEnvelopeSchema,
	cliJsonProjectsCreateEnvelopeSchema,
	cliJsonProjectsUpdateEnvelopeSchema,
	cliJsonProjectsArchiveEnvelopeSchema,
	cliJsonProjectsBindEnvelopeSchema,
	cliJsonProjectsUnbindEnvelopeSchema,
	cliJsonProjectsConnectionsListEnvelopeSchema,
	cliJsonProjectsConnectionsSetDefaultEnvelopeSchema,
	cliJsonProjectsConnectionsRemoveEnvelopeSchema,
	cliJsonAgentActivityReadEnvelopeSchema,
	cliJsonAgentActivityInstallEnvelopeSchema,
	cliJsonAgentActivityUninstallEnvelopeSchema,
	cliJsonAgentActivityStatusEnvelopeSchema,
	cliJsonAgentActivityHealthEnvelopeSchema,
	cliJsonAgentActivitySyncEnvelopeSchema,
	cliJsonAgentActivityContextEnvelopeSchema,
	cliJsonRoutinesListEnvelopeSchema,
	cliJsonRoutinesCreateEnvelopeSchema,
	cliJsonRoutinesShowEnvelopeSchema,
	cliJsonRoutinesUpdateEnvelopeSchema,
	cliJsonRoutinesPauseEnvelopeSchema,
	cliJsonRoutinesResumeEnvelopeSchema,
	cliJsonRoutinesDeleteEnvelopeSchema,
	cliJsonRoutinesRunsEnvelopeSchema
]);
/**
* The closed set of `--json` envelope `type` discriminants, in the order the
* union declares them. Useful for parity tests and contract documentation.
*/
const CLI_JSON_OUTPUT_TYPES = [
	"error",
	"daemon.run",
	"daemon.install",
	"daemon.uninstall",
	"daemon.reinstall",
	"daemon.status",
	"daemon.stop",
	"daemon.autostart",
	"agents.detect",
	"agents.list",
	"agents.show",
	"agents.enable",
	"agents.disable",
	"feedback_submitted",
	"projects.list",
	"projects.create",
	"projects.update",
	"projects.archive",
	"projects.bind",
	"projects.unbind",
	"projects.connections.list",
	"projects.connections.set_default",
	"projects.connections.remove",
	"activity.read",
	"activity.install",
	"activity.uninstall",
	"activity.status",
	"activity.health",
	"activity.sync",
	"activity.context",
	"routines.list",
	"routines.create",
	"routines.show",
	"routines.update",
	"routines.pause",
	"routines.resume",
	"routines.delete",
	"routines.runs"
];
//#endregion
export { CLI_JSON_OUTPUT_SCHEMA_VERSION, CLI_JSON_OUTPUT_TYPES, cliJsonAgentActivityContextEnvelopeSchema, cliJsonAgentActivityHealthEnvelopeSchema, cliJsonAgentActivityInstallEnvelopeSchema, cliJsonAgentActivityReadEnvelopeSchema, cliJsonAgentActivityStatusEnvelopeSchema, cliJsonAgentActivitySyncEnvelopeSchema, cliJsonAgentActivityUninstallEnvelopeSchema, cliJsonAgentsDetectEnvelopeSchema, cliJsonAgentsDisableEnvelopeSchema, cliJsonAgentsEnableEnvelopeSchema, cliJsonAgentsListEnvelopeSchema, cliJsonAgentsShowEnvelopeSchema, cliJsonDaemonAutostartEnvelopeSchema, cliJsonDaemonInstallEnvelopeSchema, cliJsonDaemonReinstallEnvelopeSchema, cliJsonDaemonRunEnvelopeSchema, cliJsonDaemonStatusEnvelopeSchema, cliJsonDaemonStopEnvelopeSchema, cliJsonDaemonUninstallEnvelopeSchema, cliJsonEntryCheckBlockedErrorCodeSchema, cliJsonErrorEnvelopeSchema, cliJsonFeedbackSubmittedEnvelopeSchema, cliJsonOutputEnvelopeSchema, cliJsonProjectsArchiveEnvelopeSchema, cliJsonProjectsBindEnvelopeSchema, cliJsonProjectsConnectionsListEnvelopeSchema, cliJsonProjectsConnectionsRemoveEnvelopeSchema, cliJsonProjectsConnectionsSetDefaultEnvelopeSchema, cliJsonProjectsCreateEnvelopeSchema, cliJsonProjectsListEnvelopeSchema, cliJsonProjectsUnbindEnvelopeSchema, cliJsonProjectsUpdateEnvelopeSchema, cliJsonRoutinesCreateEnvelopeSchema, cliJsonRoutinesDeleteEnvelopeSchema, cliJsonRoutinesListEnvelopeSchema, cliJsonRoutinesPauseEnvelopeSchema, cliJsonRoutinesResumeEnvelopeSchema, cliJsonRoutinesRunsEnvelopeSchema, cliJsonRoutinesShowEnvelopeSchema, cliJsonRoutinesUpdateEnvelopeSchema };

//# sourceMappingURL=json-output.js.map